import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, effect, input } from '@angular/core';
import { TextStyleColor, TextStyleSize, TextStyleVariant } from '@sirius/typography';

import { TextHtmlComponent } from './text-html.component';
import { ITextHtmlParagraph } from '../_shared/models/text/text-html-paragraph.model';
import { ParagraphSize } from '../_shared/types/paragraph-size.type';

const PARAGRAPH_SIZE_TO_TEXT_STYLE_SIZE: Record<ParagraphSize, TextStyleSize> = {
    small: 's',
    medium: 'm',
    large: 'l',
};

const PARAGRAPH_SIZE_HOST_CLASS: Record<ParagraphSize, string> = {
    small: 'is-small',
    medium: 'is-medium',
    large: 'is-large',
};

/**
 * @deprecated Use `sirius-text-html` directly and pass `textStyleSize`.
 *
 * If you keep using this wrapper, prefer the `textStyle*`-prefixed inputs
 * (`textStyleSize`, `textStyleVariant`, `textStyleColor`) which match the
 * naming convention of `TextStyleComponent` from `@sirius/typography`.
 *
 * The legacy `size` input (`'small' | 'medium' | 'large'`) still works as a
 * deprecated alias and emits a runtime console warning.
 */
@Component({
    selector: 'sirius-text-html-paragraph',
    template: `
        <sirius-text-html
            [html]="resolvedHtml()"
            [itemprop]="resolvedItemprop()"
            [textStyleSize]="resolvedTextStyleSize()"
            [textStyleVariant]="textStyleVariant()"
            [textStyleColor]="textStyleColor()"
            [class.is-small]="paragraphSizeClass() === 'is-small'"
            [class.is-medium]="paragraphSizeClass() === 'is-medium'"
            [class.is-large]="paragraphSizeClass() === 'is-large'"></sirius-text-html>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [TextHtmlComponent],
})
export class TextHtmlParagraphComponent {
    readonly config = input<ITextHtmlParagraph | undefined>();

    readonly html = input('');
    readonly itemprop = input<string | undefined>();
    readonly textStyleSize = input<TextStyleSize>('m');
    readonly textStyleVariant = input<TextStyleVariant>('default');
    readonly textStyleColor = input<TextStyleColor>('primary');

    /** @deprecated Use `textStyleSize` instead (`'small' | 'medium' | 'large'` map to `'s' | 'm' | 'l'`). */
    readonly size = input<ParagraphSize | undefined>(undefined);

    readonly resolvedItemprop = computed(() => this.config()?.itemprop ?? this.itemprop());
    readonly resolvedHtml = computed(() => this.config()?.html ?? this.html());

    /**
     * The legacy paragraph size (`small | medium | large`) when set via
     * `size` input or `config.size`, otherwise `undefined`. Used purely to
     * preserve the existing host class behavior.
     */
    readonly resolvedParagraphSize = computed<ParagraphSize | undefined>(() => this.size() ?? this.config()?.size);

    /**
     * Final size forwarded to the typography directive. Legacy `size` /
     * `config.size` win over `textStyleSize` for back-compat.
     */
    readonly resolvedTextStyleSize = computed<TextStyleSize>(() => {
        const legacy = this.resolvedParagraphSize();
        if (legacy) return PARAGRAPH_SIZE_TO_TEXT_STYLE_SIZE[legacy];
        return this.textStyleSize();
    });

    readonly paragraphSizeClass = computed<string | null>(() => {
        const legacy = this.resolvedParagraphSize();
        return legacy ? PARAGRAPH_SIZE_HOST_CLASS[legacy] : null;
    });

    private readonly deprecationWarnings = new Set<string>();

    constructor() {
        effect(() => {
            if (this.size() !== undefined) this.warnDeprecated('size', 'textStyleSize');
        });
    }

    private warnDeprecated(oldName: string, newName: string): void {
        if (this.deprecationWarnings.has(oldName)) return;
        this.deprecationWarnings.add(oldName);
        console.warn(`[TextHtmlParagraphComponent] "${oldName}" is deprecated. Use "${newName}" instead.`);
    }
}
